/**
 * Calculates a "nice" step value for a slider based on a minimum and maximum value.
 * The algorithm aims to find a round increment (like 1, 2, 5, 10, 20, 50, 100...)
 * that results in a reasonable number of ticks on the slider.
 *
 * @param {number} min - The minimum value of the slider.
 * @param {number} max - The maximum value of the slider.
 * @param {number} [desiredSteps=15] - The approximate number of steps desired.
 * A higher value results in a smaller step (more granularity).
 * A lower value results in a larger step (less granularity).
 * @returns {number} The calculated step value. Returns 1 if min >= max.
 */
export const calculateSliderStep = (
  min: number,
  max: number,
  desiredSteps: number = 15
): number => {
  // Edge case: if the range is invalid or zero, return a default step of 1.
  if (min >= max) {
    return 1;
  }

  const range = max - min;

  // 1. Calculate the "raw" step to achieve the desired number of steps.
  // Avoid division by zero if desiredSteps is 0.
  const rawStep = range / (desiredSteps || 1);

  // 2. Calculate the order of magnitude of the raw step (e.g., 10, 100, 1000).
  // Math.log10(rawStep) gives us the exponent (e.g., log10(85) ≈ 1.92).
  // Math.floor(1.92) = 1.
  // Math.pow(10, 1) = 10. This is our magnitude.
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));

  // 3. Normalize the raw step by dividing it by its magnitude.
  // e.g., rawStep = 85, magnitude = 10 -> normalizedStep = 8.5
  // e.g., rawStep = 1200, magnitude = 1000 -> normalizedStep = 1.2
  const normalizedStep = rawStep / magnitude;

  // 4. Round the normalized step up to the next "nice" number (1, 2, 5, or 10).
  let niceStep;
  if (normalizedStep <= 1) {
    niceStep = 1;
  } else if (normalizedStep <= 2) {
    niceStep = 2;
  } else if (normalizedStep <= 5) {
    niceStep = 5;
  } else {
    niceStep = 10;
  }

  // 5. Multiply the nice step by the magnitude to get the final result.
  // e.g., niceStep = 10, magnitude = 10 -> result = 100
  // e.g., niceStep = 2, magnitude = 1000 -> result = 2000
  return niceStep * magnitude;
};
